import React, { useState } from "react";
import {
  ExclamationTriangleIcon,
  LockClosedIcon,
  PhoneIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/solid";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface SwitchIntegrationModalProps {
  activeLabel: string;
  canSwitchIntegration: boolean;
  isLoading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  open: boolean;
  targetLabel: string;
}

const SwitchIntegrationModal: React.FC<SwitchIntegrationModalProps> = ({
  activeLabel,
  canSwitchIntegration,
  isLoading = false,
  onClose,
  onConfirm,
  open,
  targetLabel,
}) => {
  const [confirmText, setConfirmText] = useState("");
  const canConfirm = confirmText === "SWITCH" && !isLoading;

  const handleClose = () => {
    setConfirmText("");
    onClose();
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    setConfirmText("");
    onConfirm();
  };

  return (
    <Dialog
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
      open={open}
    >
      <DialogContent className="max-w-md" hideCloseButton>
        {!canSwitchIntegration ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col items-center gap-2">
              <LockClosedIcon className="h-8 w-8 text-violet-600 dark:text-violet-400" />
              <DialogTitle className="text-lg font-normal text-gray-900 dark:text-white">
                Admin Access Required
              </DialogTitle>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400">
              Switching from{" "}
              <span className="font-medium text-violet-600 dark:text-violet-400">
                {activeLabel}
              </span>{" "}
              to{" "}
              <span className="font-medium text-green-600 dark:text-green-400">
                {targetLabel}
              </span>{" "}
              requires administrator privileges. This action deletes all
              imported data and cannot be undone.
            </p>

            <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 p-4">
              <div className="flex items-start gap-3">
                <PhoneIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                    Contact Your Support Team
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    Please reach out to your CertainPath support team or account
                    administrator to request this change. They&apos;ll help
                    ensure a smooth transition.
                  </p>
                  <div className="space-y-1.5">
                    <p className="flex items-center gap-1.5 text-sm">
                      <EnvelopeIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      <a
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                        href="mailto:support@certainpath.com"
                      >
                        support@certainpath.com
                      </a>
                    </p>
                    <p className="flex items-center gap-1.5 text-sm">
                      <PhoneIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      <a
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                        href="tel:+18005551234"
                      >
                        (800) 555-1234
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleClose} variant="outline">
                Close
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col items-center gap-2">
              <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500" />
              <DialogTitle className="text-lg font-normal text-gray-900 dark:text-white">
                Switch to {targetLabel}?
              </DialogTitle>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400">
              You&apos;re switching from{" "}
              <span className="font-medium text-violet-600 dark:text-violet-400">
                {activeLabel}
              </span>{" "}
              to{" "}
              <span className="font-medium text-green-600 dark:text-green-400">
                {targetLabel}
              </span>
              .
            </p>

            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                Type{" "}
                <span className="font-mono font-medium text-gray-900 dark:text-white bg-gray-100 dark:bg-slate-700 px-1.5 py-0.5 rounded text-xs">
                  SWITCH
                </span>{" "}
                to confirm
              </p>
              <input
                className="w-full h-10 px-3 text-sm border border-gray-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 dark:text-white focus:outline-none focus:ring-1 focus:ring-ring"
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="SWITCH"
                value={confirmText}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                disabled={isLoading}
                onClick={handleClose}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                disabled={!canConfirm}
                onClick={handleConfirm}
              >
                {isLoading ? "Switching..." : "Switch"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SwitchIntegrationModal;
